import { Link } from 'react-router-dom';
import { ArrowRight, Star, MapPin, Clock, Flame, Leaf } from 'lucide-react';
import { SITE } from '../../data/site';
import { Reveal } from '../../components/Bits';
import { Img } from '../../components/Img';

export default function BlogPost() {
  return (
    <article className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative min-h-[50vh] flex flex-col justify-end overflow-hidden">
        <Img 
          src="https://dtwaeonhht2im.cloudfront.net/66ca5429f16878ee1b0751be93cbc866.png" 
          alt="Flaky parotta served with rich curry - best restaurants near me for Indian dining" 
          eager 
          className="absolute inset-0" 
          imgClassName="scale-105" 
        />
        <div 
          className="absolute inset-0" 
          style={{ background: 'linear-gradient(180deg, rgba(20,13,8,0.55) 0%, rgba(20,13,8,0.2) 38%, rgba(20,13,8,0.9) 100%)' }} 
        />
        <div className="wrap relative pb-14 pt-32 md:pb-20">
          <div className="max-w-3xl">
            <p className="eyebrow" style={{ color: 'var(--color-gold)' }}>Dining Guide</p>
            <h1 
              className="font-display mt-3 text-white" 
              style={{ fontSize: 'clamp(2.2rem, 6vw, 3.8rem)', lineHeight: 1, textShadow: '0 2px 24px rgba(0,0,0,0.4)' }}
            >
              Best Parotta and Indian Restaurants Near Me in Coppell, TX
            </h1>
            <p 
              className="mt-5 max-w-2xl" 
              style={{ fontSize: '1.15rem', lineHeight: 1.65, color: 'rgba(255,255,255,0.9)' }}
            >
              A complete guide to finding authentic South Indian cuisine near you — with hand-slapped parottas, Chettinad curries, and traditional dishes that deliver real flavor in Coppell.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="wrap py-16 md:py-24">
        <div className="max-w-3xl mx-auto">
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "@id": "https://parottaexpressusa.com/blog/best-parotta-restaurants-coppell",
            "headline": "Best Parotta and Indian Restaurants Near Me in Coppell, TX – Authentic South Indian Dining",
            "description": "A complete guide to finding authentic South Indian cuisine near you — with hand-slapped parottas, Chettinad curries, and traditional dishes that deliver real flavor in Coppell.",
            "image": [
              "https://dtwaeonhht2im.cloudfront.net/66ca5429f16878ee1b0751be93cbc866.png",
              "https://dtwaeonhht2im.cloudfront.net/4d249a04cf6d1d973efe8de3ec78a0f5.png"
            ],
            "datePublished": "2025-01-15",
            "dateModified": "2025-01-15",
            "author": {
              "@type": "Organization",
              "name": "Parotta Express",
              "url": "https://parottaexpressusa.com/"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Parotta Express",
              "logo": {
                "@type": "ImageObject",
                "url": "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/8d073425-a70c-42c7-a3ed-1a2c0386029b/1783624268133-5j77qa.jpg"
              }
            }
          }) }} />

          {/* Intro */}
          <Reveal className="space-y-6">
            <p style={{ fontSize: '1.1rem', lineHeight: 1.75, color: 'var(--color-ink-2)' }}>
              Looking for a great restaurant near me that serves authentic South Indian cuisine? Whether you're craving flaky parottas, crispy dosas, fragrant biryanis, or fiery Chettinad curries, Coppell, Texas, has excellent options for authentic Indian dining. In this guide, we'll help you find the best places for Indian food near you — with everything you need to know about hours, location, menu highlights, and what makes each restaurant special.
            </p>

            <p style={{ fontSize: '1.1rem', lineHeight: 1.75, color: 'var(--color-ink-2)' }}>
              Whether you're searching for "restaurant near me open now," "restaurant near me dine-in," or "restaurant near me takeout," this guide will connect you with authentic flavors and genuine hospitality.
            </p>
          </Reveal>

          {/* Section: What to Look For */}
          <Reveal className="mt-12">
            <h2 className="font-display text-3xl text-ink" style={{ lineHeight: 1.2 }}>
              What to Look for in an Authentic South Indian Restaurant
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: 1.75, color: 'var(--color-ink-2)', marginTop: '1.5rem' }}>
              Finding the right restaurant near you means knowing what signals quality and authenticity. Here's what separates a great South Indian restaurant from the rest:
            </p>

            <ul className="mt-6 space-y-4">
              <li className="flex items-start gap-3">
                <span className="grid place-items-center w-6 h-6 rounded-full mt-1 shrink-0" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}>
                  <Flame size={14} />
                </span>
                <div>
                  <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>Hand-Rolled Parottas Made to Order</h3>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                    Real parottas are slapped thin, folded into countless layers, and griddled fresh. They should arrive warm, flaky, and rich — never pre-made or frozen. This is the hallmark of genuine South Indian cooking.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <span className="grid place-items-center w-6 h-6 rounded-full mt-1 shrink-0" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}>
                  <Leaf size={14} />
                </span>
                <div>
                  <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>Whole Spice Blends, Not Shortcuts</h3>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                    Authentic Chettinad and regional South Indian curries start with whole spices — cumin, fenugreek, black pepper, fenugreek leaves — that are roasted and ground. The result is complex, layered heat, not just generic curry powder.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <span className="grid place-items-center w-6 h-6 rounded-full mt-1 shrink-0" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}>
                  <Clock size={14} />
                </span>
                <div>
                  <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>Fresh Preparation Every Day</h3>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                    Doughs, batters, and gravies should be prepped fresh daily — never held over from the previous day. This ensures flavor, texture, and food safety.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <span className="grid place-items-center w-6 h-6 rounded-full mt-1 shrink-0" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}>
                  <Star size={14} />
                </span>
                <div>
                  <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>Convenience: Dine-In, Takeout, and Delivery</h3>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                    Whether you're looking to dine in, grab takeout, or order delivery, the best restaurants near you offer flexible options and fast, reliable service.
                  </p>
                </div>
              </li>
            </ul>
          </Reveal>

          {/* Section: Finding Restaurants Near You */}
          <Reveal className="mt-12">
            <h2 className="font-display text-3xl text-ink" style={{ lineHeight: 1.2 }}>
              How to Find Restaurants Near Me: Tips for Your Search
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: 1.75, color: 'var(--color-ink-2)', marginTop: '1.5rem' }}>
              When searching for "restaurant near me," "restaurant near me open now," or "restaurant near me within 0.2 mi," here's what to prioritize:
            </p>

            <div className="mt-6 space-y-4">
              <div className="card p-6" style={{ background: 'var(--color-surface-2)', borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>Check Hours First</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  Search "restaurant near me open now" or check Google Maps to see real-time hours. Many South Indian restaurants are open afternoons (12 PM onward) and close late, perfect for lunch, dinner, or late-night cravings.
                </p>
              </div>

              <div className="card p-6" style={{ background: 'var(--color-surface-2)', borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>Look at the Menu Online</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  The best restaurants near you will have their menu clearly visible online. Look for specific dish names (like "Dindugal Mutton Biryani" or "Chettinad Chicken") rather than generic descriptions.
                </p>
              </div>

              <div className="card p-6" style={{ background: 'var(--color-surface-2)', borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>Read Recent Reviews</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  Search "parotta express reviews" or "parotta express photos" to see what other diners are experiencing. Photos and recent reviews tell you about food quality, portion sizes, and service speed.
                </p>
              </div>

              <div className="card p-6" style={{ background: 'var(--color-surface-2)', borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-semibold text-ink" style={{ fontSize: '1rem' }}>Choose Your Service Style</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  Looking for "restaurant near me dine-in"? Sit down and enjoy the full experience. Prefer "restaurant near me takeout"? Many restaurants have systems for quick pickup. Or search "restaurant near me for delivery" for door-to-door convenience.
                </p>
              </div>
            </div>
          </Reveal>

          {/* Section: Featured Restaurant */}
          <Reveal className="mt-12">
            <h2 className="font-display text-3xl text-ink" style={{ lineHeight: 1.2 }}>
              Parotta Express: Your Best Choice for Authentic South Indian Cuisine
            </h2>

            <div className="mt-8 grid gap-8 md:grid-cols-[1fr_1fr] items-center">
              <div>
                <p style={{ fontSize: '1.05rem', lineHeight: 1.75, color: 'var(--color-ink-2)' }}>
                  If you're searching for "parotta express near me" or simply looking for the best Indian restaurant in Coppell, Parotta Express is the answer. Since its opening, it has become the go-to destination for authentic South Indian cuisine — featuring hand-slapped parottas, Chettinad curries, and traditional dishes that honor regional South Indian cooking.
                </p>

                <div className="mt-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin size={18} style={{ color: 'var(--color-accent)', marginTop: '0.25rem' }} className="shrink-0" />
                    <div>
                      <p className="font-semibold text-ink text-sm">{SITE.address.line1}</p>
                      <p className="text-ink-2 text-sm">{SITE.address.line2}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Clock size={18} style={{ color: 'var(--color-accent)' }} className="shrink-0" />
                    <div>
                      <p className="font-semibold text-ink text-sm">Open Daily</p>
                      <p className="text-ink-2 text-sm">Mon–Thu & Sun · 12 PM – 11 PM</p>
                      <p className="text-ink-2 text-sm">Fri–Sat · 12 PM – 12 AM</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Star size={18} style={{ color: 'var(--color-accent)' }} className="shrink-0" />
                    <p className="text-ink font-semibold text-sm">Dine-In · Takeout · Online Ordering</p>
                  </div>
                </div>

                <Link to="/menu" className="btn btn-primary mt-8">
                  View Full Menu <ArrowRight size={16} />
                </Link>
              </div>

              <Img 
                src="https://dtwaeonhht2im.cloudfront.net/4d249a04cf6d1d973efe8de3ec78a0f5.png" 
                alt="A lavish platter of Dindugal mutton biryani at Parotta Express" 
                className="rounded-[var(--pe-radius-lg)]" 
                imgClassName="hover:scale-[1.03]" 
              />
            </div>
          </Reveal>

          {/* Section: Signature Dishes */}
          <Reveal className="mt-12">
            <h2 className="font-display text-3xl text-ink" style={{ lineHeight: 1.2 }}>
              Signature Dishes Worth Ordering
            </h2>

            <div className="mt-8 space-y-6">
              <div className="card p-6" style={{ borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-display text-xl text-ink">Hand-Slapped Parotta with Salna</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  The house specialty. Dough is slapped paper-thin, folded into countless buttery layers, then griddled fresh to order. Served with a rich curry for tearing and dipping — choose from veg, egg, chicken, or mutton gravy.
                </p>
              </div>

              <div className="card p-6" style={{ borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-display text-xl text-ink">Dindugal Mutton Biryani</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  Short-grain seeraga samba rice sealed with tender mutton and spices, then slow-cooked on dum (covered pot) for those deep, complex flavors. Fragrant, layered, and absolutely authentic.
                </p>
              </div>

              <div className="card p-6" style={{ borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-display text-xl text-ink">Chettinad Chicken Curry</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  Tender chicken cooked in a rich sauce made from whole roasted spices — cumin, fenugreek, black pepper, and curry leaves. Spice-forward, not hot, with real regional depth.
                </p>
              </div>

              <div className="card p-6" style={{ borderRadius: 'var(--pe-radius)' }}>
                <h3 className="font-display text-xl text-ink">Crispy Dosa with Sambar & Chutneys</h3>
                <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '0.5rem' }}>
                  Paper-crisp fermented crêpe served with hot sambar (lentil-vegetable curry) and fresh coconut chutney. A classic South Indian breakfast that works anytime.
                </p>
              </div>
            </div>
          </Reveal>

          {/* FAQ Section */}
          <Reveal className="mt-12">
            <h2 className="font-display text-3xl text-ink" style={{ lineHeight: 1.2 }}>
              Frequently Asked Questions
            </h2>

            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                {
                  "@type": "Question",
                  "name": "What does 'restaurant near me' typically mean?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "When you search 'restaurant near me,' you're typically looking for dining options within a short distance — usually within 1 to 5 miles. Your phone uses GPS to find places nearby. Searches like 'restaurant near me within 0.2 mi' are more specific, looking for options within about 3 blocks."
                  }
                },
                {
                  "@type": "Question",
                  "name": "How do I find a restaurant near me that's open now?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Use Google Maps or Yelp and search 'restaurant near me open now.' The results show real-time hours, letting you know which restaurants are currently serving. Google's 'Open' filter shows only places with current or upcoming availability."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What's the difference between dine-in, takeout, and delivery?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "'Dine-in' means you sit at the restaurant and enjoy your meal there. 'Takeout' means you order ahead and pick it up yourself. 'Delivery' means the restaurant or a delivery partner brings your food to your address. Parotta Express offers all three options."
                  }
                },
                {
                  "@type": "Question",
                  "name": "What makes Parotta Express different from other Indian restaurants?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Parotta Express specializes in authentic South Indian cuisine with hand-slapped parottas made fresh to order, whole spice blends in Chettinad curries, and daily-prepared doughs and batters. Every dish is made from scratch with regional technique and traditional flavors."
                  }
                },
                {
                  "@type": "Question",
                  "name": "Can I order online for pickup or delivery?",
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes. Parotta Express allows online ordering directly through our website, as well as through partners like UberEats, DoorDash, Grubhub, and Toast. You can choose pickup for convenience or delivery to your door."
                  }
                }
              ]
            }) }} />

            <div className="mt-8 space-y-4">
              {[
                {
                  q: "What does 'restaurant near me' typically mean?",
                  a: "When you search 'restaurant near me,' you're typically looking for dining options within a short distance — usually within 1 to 5 miles. Your phone uses GPS to find places nearby. Searches like 'restaurant near me within 0.2 mi' are more specific, looking for options within about 3 blocks."
                },
                {
                  q: "How do I find a restaurant near me that's open now?",
                  a: "Use Google Maps or Yelp and search 'restaurant near me open now.' The results show real-time hours, letting you know which restaurants are currently serving. Google's 'Open' filter shows only places with current or upcoming availability."
                },
                {
                  q: "What's the difference between dine-in, takeout, and delivery?",
                  a: "'Dine-in' means you sit at the restaurant and enjoy your meal there. 'Takeout' means you order ahead and pick it up yourself. 'Delivery' means the restaurant or a delivery partner brings your food to your address. Parotta Express offers all three options."
                },
                {
                  q: "What makes Parotta Express different?",
                  a: "Parotta Express specializes in authentic South Indian cuisine with hand-slapped parottas made fresh to order, whole spice blends in Chettinad curries, and daily-prepared doughs and batters. Every dish is made from scratch with regional technique."
                },
              ].map((item, i) => (
                <details key={i} className="card p-6 group cursor-pointer" style={{ borderRadius: 'var(--pe-radius)' }}>
                  <summary className="flex items-center justify-between font-semibold text-ink text-base" style={{ listStyle: 'none' }}>
                    {item.q}
                    <span className="transition-transform group-open:rotate-180" style={{ fontSize: '1.5rem' }}>›</span>
                  </summary>
                  <p style={{ fontSize: '0.95rem', lineHeight: 1.6, color: 'var(--color-ink-2)', marginTop: '1rem' }}>
                    {item.a}
                  </p>
                </details>
              ))}
            </div>
          </Reveal>

          {/* CTA */}
          <Reveal className="mt-12 text-center">
            <h2 className="font-display text-3xl text-ink" style={{ lineHeight: 1.2 }}>
              Ready to Find Your Favorite South Indian Dishes?
            </h2>
            <p style={{ fontSize: '1.05rem', lineHeight: 1.75, color: 'var(--color-ink-2)', marginTop: '1.5rem' }}>
              Stop searching "restaurant near me." You've found it. Browse our full menu, order online for pickup or delivery, or visit us in person at {SITE.address.line1} in Coppell.
            </p>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Link to="/menu" className="btn btn-primary">
                Browse Menu <ArrowRight size={16} />
              </Link>
              <Link to="/visit" className="btn btn-ghost">
                Visit Us
              </Link>
            </div>
          </Reveal>

          {/* Related Blog Post */}
          <Reveal className="mt-16 pt-12 border-t" style={{ borderColor: 'var(--color-line)' }}>
            <h3 className="font-semibold text-ink">Related Reading:</h3>
            <ul className="mt-4 space-y-2">
              <li>
                <Link 
                  to="/blog/best-indian-restaurant-omaha-council-bluffs" 
                  className="inline-flex items-center gap-2 text-ink font-medium hover:opacity-70 transition-opacity"
                >
                  Authentic Indian Restaurant in Omaha & Council Bluffs <ArrowRight size={16} />
                </Link>
              </li>
            </ul>
          </Reveal>
        </div>
      </section>
    </article>
  );
}
